import { useCustomForm } from "@/hooks/useCustomForm"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  IconButton,
} from "@mui/material"
import Grid from "@mui/material/Grid2"
import ClearIcon from "@mui/icons-material/Clear"
import { z } from "zod"
import { Textarea } from "@mantine/core"
import SingleSelect from "@/components/Select/SingleSelect"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useOrderCrudContext } from "@/context/orderCrudContext"
import usePaymentMethods from "@/hooks/usePaymentMethods"
import LetterAvatar from "@/components/Avatar/LetterAvatar"
import AddIcon from "@mui/icons-material/Add"
import AddContactModal from "../customers/Model"
import AddPaymentModal from "../payment-methods/Model"
import Productable from "@/components/Products/Productable"
import SecondaryButton from "@/components/Buttons/SecondaryButton"
import PrimaryButton from "@/components/Buttons/PrimaryButton"
import { DevTool } from "@hookform/devtools"
import { useNavigate } from "react-router-dom"
import { useShortcut } from "@/hooks/useShortcut"
import { ToggleButtonGroup, ToggleButton } from "@mui/material"
import TertiaryButton from "@/components/Buttons/TertiaryButton"
import { Dialog, DialogTitle, DialogContent } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { useNow } from "@mui/x-date-pickers/internals"
import dayjs from "dayjs"
import { useCustomers } from "@/hooks/useCustomers"
import { useAppDispatch, useAppSelector } from "@/lib/hooks"
import { useCreateOrder } from "@/hooks/orders/useCreateOrder"
import { OrderPaymentMethod } from "./OrderPaymentMethod"
import BillingDetails from "@/components/Products/BillingDetails"
import {
  setCustomerId,
  setGlobalDiscountAmount,
  setGlobalDiscountPercent,
  setDate,
  setNote,
  resetOrder,
  setOrderForEdit,
} from "@/redux/slices/orderFormSlice"
import FormErrorsTop from "@/components/Forms/FormErrorTop"
import CustomAutoComplete from "@/components/Select/CustomAutoComplete"
import {
  selectIsSplitPaymentValid,
  selectSplitPaymentDifference,
} from "@/redux/selectors/orderSelectors"

const OrderSchema = z.object({})

const AddOrderModal = ({ open, onClose }) => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [customerSearch, setCustomerSearch] = useState("")
  const { customers, isLoading: customerLoading } = useCustomers({
    searchQuery: customerSearch,
  })
  const [newCustomer, setNewCustomer] = useState(null)
  const optionCustomers = useMemo(() => {
    if (!newCustomer) return customers

    const exists = customers.some((c) => c.id === newCustomer.id)
    if (exists) return customers

    return [newCustomer, ...customers]
  }, [customers, newCustomer])

  const [openCustomerModal, setOpenCustomerModal] = useState(false)
  const productableRef = useRef(null)
  const orderState = useAppSelector((state) => state.order)

  const {
    openOrderDialog,
    setOpenOrderDialog,
    selectedOrderRow,
    setSelectedOrderRow,
  } = useOrderCrudContext()

  const defaultOnClose = () => {
    setOpenOrderDialog(false)
    setSelectedOrderRow(null)
    dispatch(resetOrder())
  }

  useEffect(() => {
    if (selectedOrderRow) {
      dispatch(setOrderForEdit(selectedOrderRow))
    }
  }, [selectedOrderRow])

  useEffect(() => {
    if (
      openOrderDialog &&
      customers.length > 0 &&
      !selectedOrderRow &&
      orderState.customerId !== 1
    ) {
      dispatch(setCustomerId(1))
    }
  }, [openOrderDialog, customers.length])

  // Sync form values when order state changes (for edit mode)
  const {
    api,
    register,
    handleSubmit,
    errors,
    isSubmitting,
    reset,
    watch,
    setValue,
    control,
    Controller,
    getValues,
  } = useCustomForm({
    resolver: zodResolver(OrderSchema),
  })
  useEffect(() => {
    if (selectedOrderRow && orderState.paymentMethod) {
      setValue("payment_method", orderState.paymentMethod)
    }
  }, [orderState.paymentMethod, selectedOrderRow, setValue])

  const {
    mutate: createOrder,
    error: formError,
    isError: isFormError,
  } = useCreateOrder()

  const isSplitValid = useAppSelector(selectIsSplitPaymentValid)
  const splitDiff = useAppSelector(selectSplitPaymentDifference)
  const canSubmit = !orderState.splitBilling || isSplitValid

  const onSubmit = async (data) => {
    if (orderState.splitBilling && !isSplitValid) {
      toast.error(`Split payment mismatch. Remaining: ${splitDiff}`)
      return
    }

    const payload = {
      customer_id: orderState.customerId,
      date: orderState.date,
      order_items: orderState.items,
      total_amount: orderState.totalAmount,
      cash_received: orderState.cashReceived,
      change: orderState.change,
      discount_amount: orderState.discountAmount,
      discount_percent: orderState.discountPercent,
      payment_method: orderState.paymentMethod,
      split_billing: orderState.splitBilling,
      split_payments: orderState.splitPayments,
      subtotal: orderState.subtotal,
      total_discount_amount: orderState.totalDiscountAmount,
      note: orderState.note,
    }

    // Remove keys where value is null or empty array
    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === null ||
        (Array.isArray(payload[key]) && payload[key].length === 0)
      ) {
        delete payload[key]
      }
    })

    createOrder(
      {
        orderData: payload,
        orderId: selectedOrderRow?.id,
      },
      {
        onSuccess: (response) => {
          setOpenOrderDialog(false)
          // Only reset and redirect for new orders, not updates
          if (!selectedOrderRow?.id) {
            dispatch(resetOrder())
            // Response structure: { status, message, data: { id, ... } }
            const orderId = response?.data?.id
            if (orderId) {
              navigate(`/invoice/${orderId}`)
            }
          }
        },
      }
    )
  }

  return (
    <>
      <Modal
        open={open ? open : openOrderDialog}
        onClose={defaultOnClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            margin: "100px auto",
            bgcolor: "white",
            width: { md: "80vw" },
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: 1,
            boxShadow: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: 1,
              background: "#f6f7f9",
              padding: { xs: "15px", md: "15px" },
            }}
          >
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                fontWeight: "600",
                fontSize: { xs: "16px", md: "18px" },
              }}
              className="text-black"
            >
              {selectedOrderRow ? "Update Order " : "Add New Sales "}
            </Typography>
            <IconButton
              aria-label="remove"
              size="small"
              onClick={onClose ? onClose : defaultOnClose}
            >
              <ClearIcon />
            </IconButton>
          </Box>

          <Box
            sx={{ padding: 2 }}
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            {isFormError && <FormErrorsTop errors={formError.error} />}
            <div className="w-full flex flex-row items-start gap-2 mb-4">
              {/* Customer Select Dropdown */}
              <div className="flex-grow">
                <FormControl className="w-full">
                  <CustomAutoComplete
                    className="w-full"
                    loading={customerLoading}
                    label="Customer"
                    selectedValue={orderState.customerId}
                    options={optionCustomers}
                    optionLabel={(option) => `${option.name} (${option.phone})`}
                    onChange={(value) => {
                      dispatch(setCustomerId(value || null))
                    }}
                    optionValue="id"
                    renderOptionContent={(option) => (
                      <LetterAvatar
                        name={option?.name}
                        subtitle={option?.phone}
                        key={option.id}
                      />
                    )}
                    onInputChange={(searchText, value, reason) => {
                      if (reason === "input") {
                        setCustomerSearch(searchText)
                      }
                    }}
                  />
                </FormControl>
              </div>

              {/* Add Customer Button */}
              <div className="h-full">
                <Button
                  onClick={() => setOpenCustomerModal(true)}
                  variant="outlined"
                  color="primary"
                  sx={{
                    textTransform: "capitalize",
                    borderRadius: "7px",
                    height: "100%",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                  }}
                >
                  <AddIcon />
                  <span className="ml-1">Add</span>
                </Button>
              </div>

              <div className=" ">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="date"
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        label="Date"
                        value={
                          orderState.date ? dayjs(orderState.date) : dayjs()
                        }
                        onChange={(newValue) => {
                          const formattedDate =
                            dayjs(newValue).format("YYYY-MM-DD")
                          dispatch(setDate(formattedDate))
                        }}
                        sx={{
                          width: "100%",
                          "& fieldset": {
                            border: "1px solid #D5D9E2",
                            borderRadius: "7px",
                          },
                        }}
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </div>
            </div>

            {/* Productable Component */}
            <Productable
              className="w-full"
              label="Sales Items"
              name="order_items"
              controller={Controller}
              control={control}
              errors={errors}
              defaultProducts={[]}
              helperText={errors.order_items?.message}
              onClose={onClose}
            />

            <div className="flex gap-2 py-4">
              <div className="flex-[2] flex flex-col">
                <OrderPaymentMethod
                  controller={Controller}
                  control={control}
                  errors={errors}
                  register={register}
                  setValue={setValue}
                />
              </div>
              <div className="flex flex-1">
                <BillingDetails
                  className="flex-1"
                  subtotal={orderState.subtotal}
                  total={orderState.totalAmount}
                  discount={orderState.discountAmount}
                  discountPercent={orderState.discountPercent}
                  totalDiscountAmount={orderState.totalDiscountAmount}
                  handleDiscountChange={(event) =>
                    dispatch(
                      setGlobalDiscountAmount(Number(event.target.value))
                    )
                  }
                  handleDiscountPercentChange={(event) =>
                    dispatch(
                      setGlobalDiscountPercent(Number(event.target.value))
                    )
                  }
                />
              </div>
            </div>

            <Grid size={12} sx={{ marginBottom: 2 }}>
              <Textarea
                placeholder="Note"
                value={orderState.note}
                onChange={(event) => dispatch(setNote(event.target.value))}
                error={!!errors.notes}
                helperText={errors.notes?.message}
              />
            </Grid>
            {orderState.splitBilling && !isSplitValid && (
              <p className="text-red-500 tw-text-sm">
                Remaining Amount: {splitDiff}
              </p>
            )}
            <div>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <div className="flex gap-3">
                  <PrimaryButton
                    label={selectedOrderRow ? "Update " : "Create"}
                    isSubmitting={isSubmitting}
                    disabled={!canSubmit}
                    onClick={handleSubmit(onSubmit)}
                  />

                  <SecondaryButton label="Cancel" onClick={defaultOnClose} />
                </div>
              </Box>
            </div>
            <DevTool control={control} />
          </Box>
        </Box>
      </Modal>
      <AddContactModal
        open={openCustomerModal}
        onClose={() => {
          setOpenCustomerModal(false)
          queryClient.invalidateQueries("customer-all")
        }}
        getNewRecord={(newCus) => {
          if (orderState.customerId !== newCus.id) {
            dispatch(setCustomerId(newCus.id))
            setNewCustomer(newCus)
          }
        }}
      />
    </>
  )
}

export default AddOrderModal
